import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  ShoppingCart,
  Coffee,
  LogOut,
  Plus,
  ChevronRight,
  RefreshCw,
  Search,
  ArrowRightLeft,
  Users,
  X,
  CheckCircle,
  CreditCard,
  Unlock,
  WifiOff,
} from 'lucide-react';
import { useGarcomAuth } from '../contexts/GarcomAuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useModal } from '../hooks/useScrollLock';
import ComandaSearchModal, { clearComandasCache } from '../components/ComandaSearchModal';
import BottomSheet from '../components/BottomSheet';
import GarcomBottomNav from '../components/GarcomBottomNav';
import NetworkStatusBanner from '../components/NetworkStatusBanner';
import api from '../services/api';
import toast from 'react-hot-toast';
import { normalizeString, formatarMoeda } from '../utils/stringUtils';

interface Mesa {
  id: number;
  numero: string;
  nome: string;
  capacidade: number;
}

interface ComandaMesa {
  id: number;
  comanda_fisica_id?: number | null;
  mesa_id: number | null;
  mesa_numero: string;
  mesa_nome: string;
  comanda_numero: string;
  comanda_nome: string;
  status: string;
  total: number;
  garcom_nome: string;
}

interface ComandaFisica {
  id: number;
  numero: string;
  nome: string;
  ativo: boolean;
}

const getInitials = (name: string) => {
  if (!name) return '?';
  const parts = name.split(/[\s._@-]/).filter(Boolean);
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || name[0].toUpperCase();
};

const GarcomHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { garcom, logout, login, switchUser } = useGarcomAuth();
  const { theme } = useTheme();
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [comandas, setComandas] = useState<ComandaMesa[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal de nova comanda avulsa
  const [showNovaAvulsaModal, setShowNovaAvulsaModal] = useState(false);
  // Modal de selecionar comanda física para mesa
  const [showComandaFisicaModal, setShowComandaFisicaModal] = useState(false);

  const [abrindoMesa, setAbrindoMesa] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<'todas' | 'livre' | 'ocupada'>('todas');

  // Bottom Sheet da mesa selecionada
  const [mesaSelecionada, setMesaSelecionada] = useState<Mesa | null>(null);
  const [showMesaSheet, setShowMesaSheet] = useState(false);

  // Modal de transferir mesa
  const [showTransferSheet, setShowTransferSheet] = useState(false);
  const [mesasLivres, setMesasLivres] = useState<Mesa[]>([]);
  const [transferindo, setTransferindo] = useState(false);

  // Modal de anexar comanda avulsa a uma mesa
  const [showAnexarModal, setShowAnexarModal] = useState(false);
  const [comandaParaAnexar, setComandaParaAnexar] = useState<ComandaMesa | null>(null);

  // Troca rápida de garçom
  const [showSenhaModal, setShowSenhaModal] = useState(false);
  const [showAddGarcomSheet, setShowAddGarcomSheet] = useState(false);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [usuarioSelecionadoTroca, setUsuarioSelecionadoTroca] = useState<any>(null);
  const [senhaTroca, setSenhaTroca] = useState('');
  const [submetendoTroca, setSubmetendoTroca] = useState(false);
  const [listaGarcons, setListaGarcons] = useState<any[]>([]);
  const [loadingListaGarcons, setLoadingListaGarcons] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const wasOfflineRef = useRef(false);

  useModal(showNovaAvulsaModal, () => setShowNovaAvulsaModal(false));
  useModal(showMesaSheet, () => setShowMesaSheet(false));
  useModal(showTransferSheet, () => setShowTransferSheet(false));
  useModal(showAnexarModal, () => setShowAnexarModal(false));
  useModal(showSenhaModal, () => setShowSenhaModal(false));
  useModal(showAddGarcomSheet, () => setShowAddGarcomSheet(false));

  const loadData = useCallback(async () => {
    try {
      const [mesasRes, comandasRes] = await Promise.all([
        api.get('/api/garcom/mesas'),
        api.get('/api/garcom/comandas'),
      ]);
      setMesas(mesasRes.data);
      setComandas(comandasRes.data.comandas || []);
      setIsOffline(false);
    } catch (e: any) {
      console.error(e);
      const isNetworkError = !e.response || e.message === 'Network Error' || e.code === 'ECONNABORTED';
      if (isNetworkError) {
        setIsOffline(true);
        toast('Conexão instável com a VPS. Tentando reconectar...', {
          icon: '📡',
          style: {
            background: '#d97706',
            color: '#fff',
          },
          id: 'vps-offline-warning-home'
        });
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (wasOfflineRef.current && !isOffline) {
      loadData();
      toast.success('Conexão restabelecida. Dados atualizados!', { id: 'connection-restored-home' });
    }
    wasOfflineRef.current = isOffline;
  }, [isOffline, loadData]);

  useEffect(() => {
    loadData();

    // Polling a cada 15 segundos para atualizar mesas/comandas silenciosamente em paralelo
    const interval = setInterval(() => {
      Promise.all([
        api.get('/api/garcom/mesas'),
        api.get('/api/garcom/comandas')
      ])
        .then(([mesasRes, comandasRes]) => {
          setMesas(mesasRes.data);
          setComandas(comandasRes.data.comandas || []);
          setIsOffline(false);
        })
        .catch(error => {
          console.error('Erro no polling da home do garçom:', error);
          const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
          if (isNetworkError) {
            setIsOffline(true);
          }
        });
    }, 15000);

    return () => clearInterval(interval);
  }, [loadData]);

  // Carregar garçons recentes do dispositivo
  useEffect(() => {
    try {
      const saved = localStorage.getItem('garcom_recent_users');
      if (saved) {
        setRecentUsers(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Erro ao carregar garçons recentes:', e);
    }
  }, [garcom]);

  const handleLogout = () => {
    logout();
    navigate('/garcom/login');
  };

  const handleSelectRecent = async (user: any) => {
    if (user.email === garcom?.email) {
      return;
    }
    if (user.password) {
      toast.loading('Alterando operador...', { id: 'troca-rapida' });
      try {
        await login(user.email, user.password, true);
        toast.success(`Operador alterado para ${user.displayName || user.username}!`, { id: 'troca-rapida' });
        await loadData();
      } catch (error: any) {
        toast.dismiss('troca-rapida');
        // Se a senha falhar (ex: foi alterada no servidor), abrir modal de senha
        setUsuarioSelecionadoTroca(user);
        setSenhaTroca('');
        setShowSenhaModal(true);
      }
    } else {
      setUsuarioSelecionadoTroca(user);
      setSenhaTroca('');
      setShowSenhaModal(true);
    }
  };

  const confirmarTrocaSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senhaTroca) {
      toast.error('Digite sua senha');
      return;
    }
    setSubmetendoTroca(true);
    try {
      await login(usuarioSelecionadoTroca.email, senhaTroca, true);
      
      // Atualizar a senha no localStorage
      const currentRecents = JSON.parse(localStorage.getItem('garcom_recent_users') || '[]');
      const updated = currentRecents.map((u: any) => {
        if (u.email === usuarioSelecionadoTroca.email) {
          return { ...u, password: senhaTroca };
        }
        return u;
      });
      localStorage.setItem('garcom_recent_users', JSON.stringify(updated));
      setRecentUsers(updated);

      toast.success(`Operador alterado para ${usuarioSelecionadoTroca.displayName || usuarioSelecionadoTroca.username}!`);
      setShowSenhaModal(false);
      setUsuarioSelecionadoTroca(null);
      setSenhaTroca('');
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Senha incorreta');
    } finally {
      setSubmetendoTroca(false);
    }
  };

  const abrirAddGarcom = async () => {
    setShowAddGarcomSheet(true);
    setLoadingListaGarcons(true);
    try {
      const res = await api.get('/api/garcom');
      const ativos = res.data.filter((g: any) => g.ativo === 1 || g.ativo === true);
      
      // Ordenar a lista alfabeticamente para facilitar a busca
      ativos.sort((a: any, b: any) => a.nome.localeCompare(b.nome));
      
      setListaGarcons(ativos);
    } catch (e) {
      toast.error('Erro ao buscar lista de garçons');
    } finally {
      setLoadingListaGarcons(false);
    }
  };

  const handleSelecionarNovoGarcom = async (g: any) => {
    setShowAddGarcomSheet(false);
    try {
      setLoadingListaGarcons(true);
      await switchUser(g.email);
      toast.success(`Operador alterado para ${g.nome.split(' ')[0]}!`);
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao trocar de usuário');
    } finally {
      setLoadingListaGarcons(false);
    }
  };

  const comandasNaMesa = (mesa: Mesa) =>
    comandas.filter((c: ComandaMesa) => c.mesa_id === mesa.id && c.status === 'ativa');

  // Clicar numa mesa: sempre mostrar o sheet de opções (livre ou ocupada)
  const handleMesaClick = (mesa: Mesa) => {
    setMesaSelecionada(mesa);
    setShowMesaSheet(true);
  };

  const abrirComandaNaMesa = async (mesa: Mesa) => {
    setAbrindoMesa(mesa.id);
    try {
      const res = await api.post('/api/garcom/comandas-mesa', { mesa_id: mesa.id });
      toast.success('Comanda aberta!');
      clearComandasCache();
      await loadData();
      navigate(`/garcom/comanda/${res.data.comanda.id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao abrir comanda');
    } finally {
      setAbrindoMesa(null);
    }
  };

  const abrirComandaComFisica = async (comandaFisica: ComandaFisica) => {
    if (!mesaSelecionada) return;
    try {
      const res = await api.post('/api/garcom/comandas-mesa', {
        mesa_id: mesaSelecionada.id,
        comanda_fisica_id: comandaFisica.id,
      });
      toast.success('Comanda aberta!');
      clearComandasCache();
      await loadData();
      navigate(`/garcom/comanda/${res.data.comanda.id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao abrir comanda');
    } finally {
      setShowComandaFisicaModal(false);
      setShowMesaSheet(false);
    }
  };

  const selecionarComandaAvulsa = async (comandaFisica: ComandaFisica) => {
    try {
      const res = await api.post('/api/garcom/comandas-balcao', {
        comanda_fisica_id: comandaFisica.id,
      });
      toast.success('Comanda avulsa aberta!');
      clearComandasCache();
      await loadData();
      navigate(`/garcom/comanda/${res.data.comanda.id}`);
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao abrir comanda');
    } finally {
      setShowNovaAvulsaModal(false);
    }
  };

  // Transferir mesa
  const abrirTransferencia = async () => {
    const livres = mesas.filter((m: Mesa) => comandasNaMesa(m).length === 0 && m.id !== mesaSelecionada?.id);
    setMesasLivres(livres);
    setShowMesaSheet(false);
    setShowTransferSheet(true);
  };

  const transferirMesa = async (destino: Mesa) => {
    if (!mesaSelecionada) return;
    const ativas = comandasNaMesa(mesaSelecionada);
    setTransferindo(true);
    try {
      for (const comanda of ativas) {
        await api.post(`/api/garcom/comandas/${comanda.id}/transferir-mesa`, {
          novaMesaId: destino.id,
        });
      }
      toast.success(`Mesa transferida para ${destino.nome}!`);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao transferir mesa');
    } finally {
      setShowTransferSheet(false);
      setMesaSelecionada(null);
      setTransferindo(false);
    }
  };

  // Desanexar comanda da mesa
  const desanexarDeMesa = async (comanda: ComandaMesa) => {
    if (!confirm(`Deseja realmente desanexar a comanda ${comanda.comanda_numero} da mesa? ela ficará avulsa.`)) return;
    try {
      await api.post(`/api/garcom/comandas/${comanda.id}/desanexar-mesa`);
      toast.success('Comanda desanexada com sucesso!');
      await loadData();
      const novasComandas = comandas.filter(c => c.mesa_id === comanda.mesa_id && c.id !== comanda.id && c.status === 'ativa');
      if (novasComandas.length === 0) {
        setShowMesaSheet(false);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao desanexar comanda');
    }
  };

  // Anexar comanda avulsa a uma mesa
  const anexarMesa = async (comanda: ComandaMesa, mesa: Mesa) => {
    try {
      setTransferindo(true);
      await api.post(`/api/garcom/comandas/${comanda.id}/transferir-mesa`, {
        novaMesaId: mesa.id,
      });
      toast.success(`Comanda anexada à ${mesa.nome}!`);
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.erro || 'Erro ao anexar comanda');
    } finally {
      setShowAnexarModal(false);
      setComandaParaAnexar(null);
      setTransferindo(false);
    }
  };

  // Filtro e busca
  const mesasFiltradas = mesas.filter((mesa) => {
    const ativas = comandasNaMesa(mesa);
    const statusReal = ativas.length > 0 ? 'ocupada' : 'livre';
    const buscaNorm = normalizeString(busca);
    const isNumeric = /^\d+$/.test(buscaNorm);

    const matchBuscaMesa = isNumeric
      ? normalizeString(mesa.numero).replace(/^0+/, '') === buscaNorm.replace(/^0+/, '')
      : (normalizeString(mesa.numero).includes(buscaNorm) || normalizeString(mesa.nome).includes(buscaNorm));

    const matchBuscaComanda = ativas.some(c =>
      isNumeric
        ? normalizeString(c.comanda_numero).replace(/^0+/, '') === buscaNorm.replace(/^0+/, '')
        : (normalizeString(c.comanda_numero).includes(buscaNorm) || normalizeString(c.comanda_nome).includes(buscaNorm))
    );

    return (matchBuscaMesa || matchBuscaComanda) && (filtro === 'todas' || filtro === statusReal);
  });

  const comandasAtivas = comandas.filter((c: ComandaMesa) => c.status === 'ativa');
  const comandasAvulsas = comandasAtivas.filter((c: ComandaMesa) => {
    const isAvulsa = !c.mesa_id;
    const buscaNorm = normalizeString(busca);
    const isNumeric = /^\d+$/.test(buscaNorm);
    const matchBusca = isNumeric
      ? normalizeString(c.comanda_numero).replace(/^0+/, '') === buscaNorm.replace(/^0+/, '')
      : (normalizeString(c.comanda_numero).includes(buscaNorm) || normalizeString(c.comanda_nome).includes(buscaNorm));
    return isAvulsa && matchBusca;
  });
  const mesasOcupadas = mesas.filter((m: Mesa) => comandasNaMesa(m).length > 0).length;
  const mesasLivresCount = mesas.length - mesasOcupadas;

  const excludeIds = useMemo(() =>
    comandas
      .map((c: ComandaMesa) => Number(c.comanda_fisica_id))
      .filter((id: number) => !isNaN(id) && id > 0),
    [comandas]
  );

  const tc = {
    bg: theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black' : 'bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200',
    card: theme === 'dark' ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-300/50',
    text: theme === 'dark' ? 'text-white' : 'text-gray-900',
    sub: theme === 'dark' ? 'text-gray-400' : 'text-gray-600',
    border: theme === 'dark' ? 'border-gray-600/30' : 'border-gray-300/50',
    input: theme === 'dark' ? 'bg-gray-800/60 text-white placeholder-gray-500 border-gray-700' : 'bg-white text-gray-900 placeholder-gray-400 border-gray-300',
    overlay: 'fixed inset-0 bg-black/60 backdrop-blur-sm z-40',
    sheet: theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
  };

  return (
    <div className={`min-h-screen ${tc.bg} flex flex-col`}>
      <NetworkStatusBanner onReconnect={loadData} />
      {/* Header */}
      <header className={`${tc.card} border-b ${tc.border} sticky top-0 z-20 shadow-sm`}>
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {/* Ícone ou Logo */}
              <div className="w-9 h-9 flex-shrink-0 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center hidden md:flex">
                <Coffee className="h-4 w-4 text-white" />
              </div>
              {/* Operador atual com botão de mudar */}
              <div className="flex items-center gap-2 min-w-0 py-1">
                {garcom && (
                  <button 
                    type="button"
                    onClick={abrirAddGarcom}
                    className="flex flex-col items-center gap-0.5 flex-shrink-0 relative cursor-pointer hover:scale-105 transition-transform"
                    title={`Operador Ativo: ${garcom.nome} (Clique para trocar)`}
                  >
                    <div className="w-9 h-9 rounded-full bg-orange-500 ring-2 ring-orange-500 ring-offset-2 ring-offset-gray-900 flex items-center justify-center text-white font-bold text-xs select-none">
                      {garcom.nome.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-[10px] text-orange-400 font-bold max-w-[60px] truncate">Mudar usr</span>
                  </button>
                )}
              </div>
            </div>

            {/* Stats rápidas */}
            <div className="flex items-center gap-2 text-xs font-semibold">
              <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                <CheckCircle className="h-3 w-3" />
                {mesasLivresCount} livres
              </span>
              <span className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full">
                <Users className="h-3 w-3" />
                {mesasOcupadas} ocup.
              </span>
              <button onClick={() => loadData()} disabled={loading} className={`p-2 rounded-lg hover:bg-black/10 transition-colors`}>
                <RefreshCw className={`h-5 w-5 ${tc.sub} ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Barra de busca + filtro */}
          <div className="mt-3 flex gap-2">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${tc.sub} h-4 w-4 pointer-events-none`} />
              <input
                type="text"
                placeholder="Buscar mesa ou comanda..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                autoComplete="off"
                className={`w-full pl-9 pr-4 py-2 border rounded-lg ${tc.input} focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-sm`}
                style={{ fontSize: '16px', color: theme === 'dark' ? '#fff' : '#111827' }}
              />
            </div>
            <div className="flex rounded-lg overflow-hidden border border-gray-600/30 text-xs font-semibold">
              {(['todas', 'livre', 'ocupada'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFiltro(f)}
                  className={`px-2.5 py-2 capitalize transition-colors ${filtro === f ? 'bg-orange-500 text-white' : `${tc.sub} hover:bg-white/10`}`}
                >
                  {f === 'todas' ? 'Todas' : f === 'livre' ? 'Livres' : 'Ocup.'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-auto pb-24 lg:pb-6">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 space-y-6">

          {/* Ação rápida: Nova Comanda Avulsa */}
          <section>
            <button
              onClick={() => setShowNovaAvulsaModal(true)}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl border-2 border-transparent hover:border-orange-400/50 transition-all active:scale-[0.98] shadow-lg touch-manipulation flex items-center gap-3 sm:gap-4 p-4"
            >
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <span className="block text-lg font-bold">Nova Comanda Avulsa</span>
                <span className="block text-sm text-white/90">Balcão / delivery (sem mesa)</span>
              </div>
              <ChevronRight className="h-5 w-5 text-white/80 flex-shrink-0" />
            </button>
          </section>

          {/* Grid de Mesas */}
          <section>
            <h2 className={`text-sm font-semibold uppercase tracking-wider ${tc.sub} mb-3 flex items-center gap-2`}>
              <Table className="h-4 w-4" />
              Mesas ({mesasFiltradas.length})
            </h2>
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className={`${tc.card} rounded-xl border h-20 animate-pulse`} />
                ))}
              </div>
            ) : mesasFiltradas.length === 0 ? (
              <div className={`${tc.card} rounded-xl border p-6 text-center ${tc.sub}`}>
                {busca ? 'Nenhuma mesa encontrada.' : 'Nenhuma mesa cadastrada.'}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {mesasFiltradas.map((mesa) => {
                  const ativas = comandasNaMesa(mesa);
                  const ocupada = ativas.length > 0;
                  const isLoading = abrindoMesa === mesa.id;
                  const totalMesa = ativas.reduce((s, c) => s + Number(c.total || 0), 0);
                  return (
                    <button
                      key={mesa.id}
                      type="button"
                      onClick={() => handleMesaClick(mesa)}
                      disabled={isLoading}
                      className={`${tc.card} rounded-xl border-2 transition-all duration-200 text-left p-3 sm:p-4 touch-manipulation active:scale-[0.98] ${ocupada ? 'border-orange-500/40 hover:border-orange-500/60' : 'border-green-500/30 hover:border-green-500/50'
                        }`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <span className={`font-bold text-base sm:text-lg truncate ${tc.text}`}>{mesa.numero}</span>
                        {isLoading ? (
                          <span className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : (
                          <ChevronRight className={`h-4 w-4 flex-shrink-0 ${tc.sub}`} />
                        )}
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${tc.sub}`}>{mesa.nome}</p>
                      {ocupada ? (
                        <>
                          <p className="text-xs text-orange-400 font-semibold mt-1">{formatarMoeda(totalMesa)}</p>
                          <p className="text-[10px] text-orange-300 mt-0.5">{ativas.length} comanda(s)</p>
                        </>
                      ) : (
                        <p className="text-xs text-green-500 dark:text-green-400 font-medium mt-1">Livre • toque p/ abrir</p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Comandas avulsas ativas */}
          {comandasAvulsas.length > 0 && (
            <section>
              <h2 className={`text-sm font-semibold uppercase tracking-wider ${tc.sub} mb-3 flex items-center gap-2`}>
                <ShoppingCart className="h-4 w-4" />
                Avulsas ativas ({comandasAvulsas.length})
              </h2>
              <div className="space-y-2">
                {comandasAvulsas.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`w-full ${tc.card} rounded-xl border p-3 sm:p-4 flex items-center justify-between gap-3 text-left transition-all hover:border-orange-500/40 touch-manipulation active:scale-[0.99]`}
                  >
                    <div className="flex-1 min-w-0" onClick={() => navigate(`/garcom/comanda/${c.id}`)}>
                      <span className={`font-semibold block truncate ${tc.text}`}>{c.comanda_nome}</span>
                      <span className={`text-sm ${tc.sub}`}>{formatarMoeda(c.total)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setComandaParaAnexar(c); setShowAnexarModal(true); }}
                        className="bg-green-500/10 text-green-500 border border-green-500/20 rounded-lg p-2 hover:bg-green-500/20 transition-all font-bold"
                        title="Anexar à uma mesa"
                      >
                        <LogOut className="h-5 w-5 rotate-[90deg]" />
                      </button>
                      <ChevronRight className={`h-5 w-5 flex-shrink-0 ${tc.sub}`} onClick={() => navigate(`/garcom/comanda/${c.id}`)} />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {/* ====== BOTTOM SHEET: AÇÕES DA MESA ====== */}
      <BottomSheet
        isOpen={showMesaSheet}
        onClose={() => setShowMesaSheet(false)}
        title={mesaSelecionada?.nome}
      >
        <div className="p-4 space-y-6">
          <p className="text-gray-400 text-sm -mt-2">
            {mesaSelecionada && comandasNaMesa(mesaSelecionada).length > 0
              ? `${comandasNaMesa(mesaSelecionada).length} comanda(s) ativa(s)`
              : 'Mesa livre — Escolha uma opção para abrir'}
          </p>

          {/* Lista de comandas ativas */}
          {mesaSelecionada && comandasNaMesa(mesaSelecionada).length > 0 && (
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest pl-1">Comandas em Aberto</h4>
              {comandasNaMesa(mesaSelecionada).map((c) => (
                <div key={c.id} className="flex gap-2">
                  <button
                    onClick={() => { setShowMesaSheet(false); navigate(`/garcom/comanda/${c.id}`); }}
                    className="flex-1 flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-orange-500/50 transition-all active:scale-[0.98]"
                  >
                    <div>
                      <p className="font-bold text-white">{c.comanda_nome}</p>
                      <p className="text-sm text-orange-400 font-bold">{formatarMoeda(c.total)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); desanexarDeMesa(c); }}
                    className="aspect-square bg-green-500/10 text-green-500 border border-green-500/20 rounded-2xl flex items-center justify-center hover:bg-green-500/20 transition-all"
                    title="Desanexar da mesa (tornar avulsa)"
                  >
                    <LogOut className="h-5 w-5 rotate-[-90deg]" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Ações */}
          <div className="space-y-3 pt-4 border-t border-white/5">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest pl-1">Ações de Atendimento</h4>

            {/* Nova comanda (sem física) - BOTÃO OCULTO A PEDIDO DO USUÁRIO
            <button
              onClick={() => { setShowMesaSheet(false); mesaSelecionada && abrirComandaNaMesa(mesaSelecionada); }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-all active:scale-[0.98]"
            >
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Plus className="h-6 w-6 text-green-400" />
              </div>
              <div className="text-left">
                <p className="font-bold text-white">Abrir Nova Comanda</p>
                <p className="text-xs text-gray-400">Lançar direto na mesa (sem cartão)</p>
              </div>
            </button>
            */}

            {/* Nova comanda com física */}
            <button
              onClick={() => { setShowMesaSheet(false); setShowComandaFisicaModal(true); }}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-orange-500/10 border border-orange-500/20 hover:bg-orange-500/20 transition-all active:scale-[0.98]"
            >
              <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-6 w-6 text-orange-400" />
              </div>
              <div className="text-left">
                <p className="font-bold text-white">Vincular Cartão/Física</p>
                <p className="text-xs text-gray-400">Usar comanda numerada</p>
              </div>
            </button>

            {/* Transferir mesa */}
            {mesaSelecionada && comandasNaMesa(mesaSelecionada).length > 0 && (
              <button
                onClick={abrirTransferencia}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-all active:scale-[0.98]"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <ArrowRightLeft className="h-6 w-6 text-blue-400" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-white">Transferir Consumo</p>
                  <p className="text-xs text-gray-400">Mover para outra mesa livre</p>
                </div>
              </button>
            )}
          </div>
        </div>
      </BottomSheet>

      {/* ====== BOTTOM SHEET: TRANSFERIR MESA ====== */}
      <BottomSheet
        isOpen={showTransferSheet}
        onClose={() => setShowTransferSheet(false)}
        title="Transferir Mesa"
      >
        <div className="p-4 space-y-4">
          <p className="text-gray-400 text-sm -mt-2 px-1">
            Selecione a mesa de destino para {mesaSelecionada?.nome}
          </p>

          {mesasLivres.length === 0 ? (
            <div className="text-center py-12 px-4 bg-white/5 rounded-2xl border border-dashed border-white/10">
              <Table className="h-10 w-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nenhuma mesa livre disponível</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
              {mesasLivres.map((m) => (
                <button
                  key={m.id}
                  onClick={() => transferirMesa(m)}
                  disabled={transferindo}
                  className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{m.nome}</p>
                    <p className="text-xs text-gray-500">Mesa {m.numero} • cap. {m.capacidade}</p>
                  </div>
                  {transferindo ? (
                    <span className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <ArrowRightLeft className="h-4 w-4 text-blue-400" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Modal: comanda avulsa */}
      <ComandaSearchModal
        isOpen={showNovaAvulsaModal}
        onClose={() => setShowNovaAvulsaModal(false)}
        onSelect={selecionarComandaAvulsa}
        title="Nova Comanda Avulsa"
        description="Selecione uma comanda física disponível para abrir um novo atendimento avulso."
        excludeIds={excludeIds}
      />

      {/* Modal: comanda física para mesa */}
      <ComandaSearchModal
        isOpen={showComandaFisicaModal}
        onClose={() => setShowComandaFisicaModal(false)}
        onSelect={abrirComandaComFisica}
        title="Selecionar Comanda"
        description="Selecione uma comanda física para esta mesa."
        excludeIds={excludeIds}
      />

      {/* ====== BOTTOM SHEET: ANEXAR COMANDA À MESA ====== */}
      <BottomSheet
        isOpen={showAnexarModal}
        onClose={() => { setShowAnexarModal(false); setComandaParaAnexar(null); }}
        title="Anexar à Mesa"
      >
        <div className="p-4 space-y-4">
          <p className="text-gray-400 text-sm -mt-2 px-1">
            Escolha uma mesa livre para anexar a comanda <b>{comandaParaAnexar?.comanda_nome}</b>
          </p>

          {mesas.filter(m => comandasNaMesa(m).length === 0).length === 0 ? (
            <div className="text-center py-12 px-4 bg-white/5 rounded-2xl border border-dashed border-white/10">
              <Table className="h-10 w-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nenhuma mesa livre disponível</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
              {mesas.filter(m => comandasNaMesa(m).length === 0).map((m) => (
                <button
                  key={m.id}
                  onClick={() => comandaParaAnexar && anexarMesa(comandaParaAnexar, m)}
                  disabled={transferindo}
                  className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{m.nome}</p>
                    <p className="text-xs text-gray-500">Mesa {m.numero} • cap. {m.capacidade}</p>
                  </div>
                  {transferindo ? (
                    <span className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Plus className="h-4 w-4 text-blue-400" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* ====== BOTTOM SHEET: SELECIONAR NOVO GARÇOM DA EMPRESA ====== */}
      <BottomSheet
        isOpen={showAddGarcomSheet}
        onClose={() => {
          if (!loadingListaGarcons) {
            setShowAddGarcomSheet(false);
            setListaGarcons([]);
          }
        }}
        title="Adicionar Operador"
      >
        <div className="p-4 space-y-4">
          <p className="text-gray-400 text-sm -mt-2 px-1">
            Selecione um garçom ativo da empresa para entrar:
          </p>

          {loadingListaGarcons ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
            </div>
          ) : listaGarcons.length === 0 ? (
            <p className="text-gray-500 text-center py-6 text-sm">
              Todos os garçons ativos já estão no topo ou não há outros garçons cadastrados.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pb-4">
              {listaGarcons.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => handleSelecionarNovoGarcom(g)}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-orange-500/30 transition-all active:scale-[0.98]"
                >
                  <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs">
                    {g.nome.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left min-w-0 flex-1">
                    <p className="font-bold text-white text-sm truncate">{g.nome} {g.sobrenome}</p>
                    <p className="text-xs text-gray-500 truncate">{g.email.split('@')[0]}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* ====== BOTTOM SHEET: MODAL DE SENHA DO SUBUSUÁRIO ====== */}
      <BottomSheet
        isOpen={showSenhaModal}
        onClose={() => {
          if (!submetendoTroca) {
            setShowSenhaModal(false);
            setUsuarioSelecionadoTroca(null);
            setSenhaTroca('');
          }
        }}
        title="Acesso do Operador"
      >
        <div className="p-4 space-y-4">
          {usuarioSelecionadoTroca && (
            <form onSubmit={confirmarTrocaSenha} className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl border border-white/10">
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-sm">
                  {usuarioSelecionadoTroca.displayName?.charAt(0).toUpperCase() || usuarioSelecionadoTroca.username?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="text-left">
                  <p className="font-bold text-white">{usuarioSelecionadoTroca.displayName || usuarioSelecionadoTroca.username}</p>
                  <p className="text-xs text-gray-400">Insira sua senha para ativar sua sessão</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Senha do Garçom
                </label>
                <input
                  type="password"
                  value={senhaTroca}
                  onChange={(e) => setSenhaTroca(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white placeholder-gray-500 border border-gray-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-sm"
                  placeholder="Digite sua senha"
                  required
                  autoFocus
                  style={{ fontSize: '16px' }}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submetendoTroca}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-2xl font-bold hover:from-orange-600 hover:to-red-600 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submetendoTroca ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : 'Confirmar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSenhaModal(false);
                    setUsuarioSelecionadoTroca(null);
                    setSenhaTroca('');
                  }}
                  disabled={submetendoTroca}
                  className="flex-1 bg-gray-800 text-gray-300 border border-gray-700 py-3 rounded-2xl font-bold hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      </BottomSheet>

      <GarcomBottomNav />
    </div>
  );
};

export default GarcomHomePage;
